// TODO - Type 'any' needs to be fixed.
/* eslint-disable no-mixed-spaces-and-tabs */
import ArrowBack from "@mui/icons-material/ArrowBack";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import { Alert, FadeProps, Skeleton, TextField } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Fade from "@mui/material/Fade";
import Snackbar from "@mui/material/Snackbar";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { GridRowId } from "@mui/x-data-grid";
import { addMinutes, differenceInMinutes } from "date-fns";
import isPast from "date-fns/isPast";
import _ from "lodash";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  assignWorkers,
  renotifyWorkers,
  resetShiftDetails,
} from "../../../../../../export/scheduler";
import {
  ASSIGN_SHIFT,
  FASTEST,
  SET_PREFERRED_WORKERS,
  SHIFT_DETAILS,
} from "../../../../../assets/constants";
import { FLEX_SCHEDULER_PERMISSIONS } from "../../../../../config/permissions";
import CheckboxPopover from "../../../../../design/checkbox-popover";
import LoadingButton from "../../../../../design/wilya-loading-button";
import { isInterestAllocationType } from "../../../../../utils/allocation-type";
import {
  formatDate,
  getConfirmedWorkersCount,
  getHourMinuteDifference,
  hasPermissionString,
} from "../../../../../utils/helpers";
import useViewport from "../../../../../utils/use-viewport";
import {
  resetAssignWorkers,
  resetAssignWorkersResponse,
  resetRemoveWorker,
  resetSetOTWorkers,
  resetSetOTWorkersResponse,
  setOTWorkers,
} from "../../../store/scheduler-slice";
import AssignShiftTable from "../../shift-details/assign-shift-table";
import ConfirmedWorkers from "../../shift-details/confirmed-workers";
import Notice from "../../shift-details/notice";
import ShiftDetailsTable from "../../shift-details/ShiftDetailsTable";
import ShiftMetaData from "../../shift-details/ShiftMetaData";
import RemoveShiftWorkerDialog from "../remove-shift-worker-dialog";
import { WorkersExtendedPropV2 } from "../shift-details-dialog/types";
import { ASSIGNED, CONFIRMED } from "./constants";
import useShiftDetailsDialogController from "./controllers/use-shift-details-dialog-controller";
import ReviewWorkerAssignShiftCheckbox from "./review-worker-assign-shift-checkbox";

interface ShiftDetailsDialogProps {
  open: boolean;
  handleClose: any;
  shiftId: any;
  isWorkerRequest: boolean;
}

interface AssignWorkerResponseProp {
  workerId: string;
  shiftId: number;
  isSuccess: boolean;
  message: string;
}

const ShiftDetailsDialog: React.FC<ShiftDetailsDialogProps> = ({
  open,
  handleClose,
  shiftId,
  isWorkerRequest,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { height } = useViewport();
  const [activeStep, setActiveStep] = useState(SHIFT_DETAILS);
  const [search, setSearch] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [activeTab, setActiveTab] = useState(0);
  const [columnAnchorEl, setColumnAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [workerSlotSelections, setWorkerSlotSelections] = useState<{ [workerId: string]: any }>({});
  const handleWorkerSlotChange = (workerId: string, slot: number, customStartTime?: string, customEndTime?: string) => {
    setWorkerSlotSelections(prev => ({
      ...prev,
      [workerId]: customStartTime && customEndTime ? {
        slot,
        customStartTime: new Date(customStartTime).toISOString(),
        customEndTime: new Date(customEndTime).toISOString()
      } : slot,
    }));
  };
  const [workersSelectedForAssignShift, setWorkersSelectedForAssignShift] =
    useState<GridRowId[]>([]);

  const permissions = useSelector(
    (state: any) => state.user.userData.permissions
  );
  // GAT-7251: these two replace the ReadOnly role test that used to stand in
  // for a permission on every assignment and renotify affordance here.
  const canManualAssignWorkers = hasPermissionString(
    permissions,
    FLEX_SCHEDULER_PERMISSIONS.MANUAL_ASSIGN_WORKERS
  );
  const canRenotifyWorkers = hasPermissionString(
    permissions,
    FLEX_SCHEDULER_PERMISSIONS.RENOTIFY_WORKERS
  );
  const [
    {
      shiftMetadata,
      jobRoleMetadata,
      workCenterMetadata,
      availableWorkersForShiftRow,
      columns,
      columnOptions,
      allSkillLevels,
      confirmedWorkersCount,
      appliedWorkersCount,
      iAmSure,
      rowsV2,
      statusOptions,
      availablePreferredWorkersForShiftRow,
      status,
      removeWorkerDialogOpen,
      removeWorkerDetail,
      remainingFilledPartialSlots,
      totalFullSpotsConfirmed,
      totalHalfSpotsConfirmed,
      totalFlexiSpotsConfirmed,
      totalFlexiSpotsTotal,
      availableSlots,
      openFlexiSlots,
    },
    {
      setAvailableWorkersForShiftRow,
      setAvailablePreferredWorkersForShiftRow,
      setRowsV2,
      setIAmSure,
      setStatusOptions,
      setColumnOptions,
      closeRemoveWorkerDialog,
      handleRemoveWorker,
    },
  ] = useShiftDetailsDialogController(
    shiftId,
    isWorkerRequest,
    search,
    activeStep,
    activeTab,
    workerSlotSelections,
    handleWorkerSlotChange,
    workersSelectedForAssignShift
  );

  // Whether any part of the shift is still open, so workers can be renotified.
  // A head count misses half-filled spots: one first-half worker on a 1-worker
  // shift leaves 0 by head count while the second half is still open.
  const hasOpenSpots = useMemo(() => {
    if (shiftMetadata?.isFlexiSignupEnabled) {
      return (openFlexiSlots?.length ?? 0) > 0;
    }
    const confirmed = confirmedWorkersCount !== undefined
      ? confirmedWorkersCount
      : getConfirmedWorkersCount(shiftMetadata);
    const openByHeadCount =
      parseInt(shiftMetadata?.numberOfWorkersNeeded) - confirmed > 0;
    const hasOpenHalf = !!shiftMetadata?.isPartialSignupEnabled
      && ((availableSlots?.firstHalf ?? 0) > 0
        || (availableSlots?.secondHalf ?? 0) > 0);
    return openByHeadCount || hasOpenHalf;
  }, [shiftMetadata, openFlexiSlots, confirmedWorkersCount, availableSlots]);

  useEffect(() => {
    if (availableSlots) {
      // Find workers that are in the selection list but don't have a slot assigned yet.
      const newlySelectedIds = workersSelectedForAssignShift.filter(
        (id) => !Object.prototype.hasOwnProperty.call(workerSlotSelections, id as string)
      );

      // If there are no new workers to process, we can exit early.
      if (newlySelectedIds.length === 0 || !availableWorkersForShiftRow) {
        return;
      }

      const newSelectionsToAssign: { [workerId: string]: number } = {};

      // NOTE: We use the availableSlots from the current render. If multiple workers
      // are selected at once, this logic might over-assign, but it will be visually
      // corrected on the subsequent render. This is a good trade-off for simplicity.

      newlySelectedIds.forEach(id => {
        const workerId = id as string;
        const worker = availableWorkersForShiftRow.find(w => w.id === workerId
          && w.errorMessage === "");

        if (!worker) return; // Should not happen, but a good safeguard

        // --- Priority 1: Assign Full Shift when a full-shift row is open, or
        // the shift supports only full-shift signups (neither partial nor flexi) ---
        if (
          availableSlots.fullShift > 0 ||
          (!shiftMetadata?.isPartialSignupEnabled &&
            !shiftMetadata?.isFlexiSignupEnabled)
        ) {
          newSelectionsToAssign[workerId] = 0; // 0 = Full Shift
          return; // Move to the next worker
        }

        // --- Flexi shifts have no half-shift concept. When no full-shift row is
        // open, leave the slot unset so the cell auto-selects the first
        // available open spot (a leftover custom window). ---
        if (shiftMetadata?.isFlexiSignupEnabled) {
          return;
        }

        // --- Check if worker is available from the start for Priority 2 ---
        const shiftStartTime = new Date(shiftMetadata?.startDateTime);
        // Use the most reliable start time available for the worker
        const workerBestStartTime = worker.workerShiftStartDateTime || worker.ftShifts?.[0]?.StartDateTime || shiftMetadata.startDateTime;
        const workerStartTime = new Date(workerBestStartTime);
        const isAvailableFromStart = differenceInMinutes(workerStartTime, shiftStartTime) <= (shiftMetadata?.overlappingSignupLimitMins || 0);

        // --- Priority 2: Assign First Half if available AND worker is eligible ---
        if (availableSlots.firstHalf > 0 && isAvailableFromStart) {
          newSelectionsToAssign[workerId] = 1; // 1 = First Half
          return;
        }

        // --- Priority 3: Assign Second Half if available ---
        if (availableSlots.secondHalf > 0) {
          newSelectionsToAssign[workerId] = 2; // 2 = Second Half
          return;
        }
      });

      // If we found any new slots to assign, update the state in one go.
      if (Object.keys(newSelectionsToAssign).length > 0) {
        setWorkerSlotSelections(prev => ({ ...prev, ...newSelectionsToAssign }));
      }
    }
  }, [
    workersSelectedForAssignShift,
    availableSlots,
    availableWorkersForShiftRow,
    shiftMetadata,
    workerSlotSelections
  ]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Leaving the assign step without assigning must discard the in-flight
  // selections; otherwise they keep occupying spots in the coverage math and
  // the Assign button stays hidden on the shift details view.
  const handleExitAssignShift = () => {
    setWorkersSelectedForAssignShift([]);
    setWorkerSlotSelections({});
    setIAmSure(false);
    setActiveStep(SHIFT_DETAILS);
  };

  const [workersSelected, setWorkersSelected] = useState<GridRowId[]>([]);
  const [workersPreferredForAssignShift, setWorkersPreferredForAssignShift] =
    useState<GridRowId[]>([]);
  const [
    originalWorkersPreferredForAssignShift,
    setOriginalWorkersPreferredForAssignShift,
  ] = useState<GridRowId[]>([]);
  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<
    number[]
  >([]);

  const errorMessage = useSelector(
    (state: any) => state.supervisor?.shiftDetailsV2?.errorMessage
  );

  const removeWorkerStatus = useSelector(
    (state: any) => state.supervisor?.removeWorker?.status
  );

  const renotifyWorkersStatus = useSelector(
    (state: any) => state.supervisor?.renotifyWorkers.status
  );

  const assignWorkersStatus = useSelector(
    (state: any) => state.supervisor?.assignWorkers.status
  );

  const assignWorkersResponse: AssignWorkerResponseProp[] = useSelector(
    (state: any) => state.supervisor?.assignWorkers.response
  );

  const enableManualAssignment = useSelector(
    (state: any) =>
      state.supervisor?.shiftDetailsV2?.data?.enableManualAssignment
  );

  const manualForceAssignWorkerTypeIds: number[] | undefined = useSelector(
    (state: any) =>
      state.supervisor?.shiftDetailsV2?.data?.manualForceAssignWorkerTypeIds
  );

  const getOTWorkersResponse = useSelector(
    (state: any) => state.supervisor?.getOTWorkers.response
  );

  const getOTWorkersStatus = useSelector(
    (state: any) => state.supervisor?.getOTWorkers.status
  );

  const setOTWorkersStatus = useSelector(
    (state: any) => state.supervisor?.setOTWorkers.status
  );

  const setOTWorkersResponse = useSelector(
    (state: any) => state.supervisor?.setOTWorkers.response
  );

  const setOTWorkersWorkersPreferredForAssignShift = useSelector(
    (state: any) =>
      state.supervisor?.setOTWorkers.workersPreferredForAssignShift
  );

  const handleSnackbarEnter = () => {
    setOriginalWorkersPreferredForAssignShift(
      setOTWorkersWorkersPreferredForAssignShift
    );
  };

  const Transition = (props: FadeProps) => {
    return <Fade {...props} onEnter={handleSnackbarEnter} />;
  };

  const isRequirementFulfilled = () => {
    if (shiftMetadata?.isFlexiSignupEnabled) {
      return openFlexiSlots.length === 0;
    }
    return confirmedWorkersCount !== undefined
      ? parseInt(shiftMetadata.numberOfWorkersNeeded) - confirmedWorkersCount ===
          0
      : parseInt(shiftMetadata?.numberOfWorkersNeeded) -
          getConfirmedWorkersCount(shiftMetadata) ===
          0;
  };

  const canShowNoticeForForceAssign = () => {
    try {
      const requirementFulfilled = isRequirementFulfilled();
      return (
        !requirementFulfilled && canManualAssignWorkers && enableManualAssignment
      );
    } catch (e) {
      return false;
    }
  };

  const canShowWorkerPreferences = () => {
    try {
      const requirementFulfilled = isRequirementFulfilled();
      const beforeShiftStartWindow =
        getHourMinuteDifference(new Date(shiftMetadata?.startDate || shiftMetadata?.startDateTime), new Date())
          .differenceInMinutes > 0;
      const allowedShiftAllocationType = isInterestAllocationType(
        shiftMetadata?.shiftAllocationType
      );
      // The rule allows at least one worker type for supervisor assignment
      const manualForceAssignEnabled =
        (manualForceAssignWorkerTypeIds?.length ?? 0) > 0;
      const closedForInterest = shiftMetadata?.windowClosingTime
        ? new Date().getTime() >=
          new Date(shiftMetadata.windowClosingTime).getTime()
        : false;
      return (
        !requirementFulfilled &&
        canManualAssignWorkers &&
        beforeShiftStartWindow &&
        !closedForInterest &&
        manualForceAssignEnabled &&
        allowedShiftAllocationType &&
        getOTWorkersStatus === "fulfilled"
      );
    } catch (e) {
      return false;
    }
  };

  const getAssignedWorkerSuccessMessage = () => {
    let returnValue = "";
    if (Array.isArray(assignWorkersResponse)) {
      const successWorkers = assignWorkersResponse.filter(
        (assignWorker) => assignWorker.isSuccess
      );
      if (successWorkers.length > 0) {
        returnValue =
          successWorkers.length > 1
            ? t("scheduler_shift-assigned-workers-plural", {
                n: successWorkers.length,
              })
            : t("scheduler_shift-assigned-workers-singular", {
                n: successWorkers.length,
              });
      } else {
        returnValue = "";
      }
    }
    return returnValue;
  };

  const getPreferredWorkerAssignedSuccessMessage = () => {
    let returnValue = "";
    if (Array.isArray(setOTWorkersResponse)) {
      const successfullyAddedWorkers = setOTWorkersResponse.filter(
        (assignWorker) => assignWorker.decision === 1
      );
      const successfullyRemovedWorkers = setOTWorkersResponse.filter(
        (assignWorker) => assignWorker.decision === 2
      );
      const noChangeWorkers = setOTWorkersResponse.filter(
        (assignWorker) => assignWorker.decision === 0
      );
      const stats = [];
      if (successfullyAddedWorkers.length > 0) {
        stats.push(t("scheduler_added-count", { n: successfullyAddedWorkers.length }));
      }
      if (successfullyRemovedWorkers.length > 0) {
        stats.push(t("scheduler_removed-count", { n: successfullyRemovedWorkers.length }));
      }
      if (noChangeWorkers.length > 0) {
        stats.push(t("scheduler_unchanged-count", { n: noChangeWorkers.length }));
      }
      returnValue = t("scheduler_updated-workers-forced-overtime", {
        stats: stats.length > 0 ? `(${stats.join(", ")})` : "",
      });
    }
    return returnValue;
  };

  const getWorkerCountTextForAssignShift = () => {
    const needed = parseInt(shiftMetadata?.numberOfWorkersNeeded) || 0;
    const confirmed = confirmedWorkersCount !== undefined
      ? confirmedWorkersCount
      : getConfirmedWorkersCount(shiftMetadata) || 0;

    const remaining = needed - confirmed;
    const spotsText = (count: number) =>
      count > 1
        ? t("scheduler_spots-plural", { n: count })
        : t("scheduler_spots-singular", { n: count });
    const fullSpotsText = (count: number) =>
      count > 1
        ? t("scheduler_full-spots-plural", { n: count })
        : t("scheduler_full-spots-singular", { n: count });
    const and = ` ${t("scheduler_and")} `;

    let partialSignUpString = "";
    if (shiftMetadata?.isFlexiSignupEnabled) {
      const fullOpen = openFlexiSlots.filter((s) => s.isFullSpot).length;
      const partialOpen = openFlexiSlots.filter((s) => !s.isFullSpot).length;
      const parts = [];
      if (fullOpen > 0)
        parts.push(fullSpotsText(fullOpen));
      if (partialOpen > 0)
        parts.push(
          partialOpen > 1
            ? t("scheduler_partial-spots-plural", { n: partialOpen })
            : t("scheduler_partial-spots-singular", { n: partialOpen })
        );
      partialSignUpString = parts.length > 0 ? parts.join(and) : "";
      if (partialSignUpString) {
        return t("scheduler_spots-still-open-notify", { spots: partialSignUpString });
      }
    }

    if (shiftMetadata?.isPartialSignupEnabled) {
      const fullSpotsRemaining = needed - (confirmedWorkersCount ?? shiftMetadata?.slotsFilled) - (remainingFilledPartialSlots ?? 0);
      const partialSpotsRemaining = remainingFilledPartialSlots ?? 0;
      const partialSpotsTextValue = partialSpotsRemaining > 0
        ? (partialSpotsRemaining > 1
            ? t("scheduler_partial-spots-plural", { n: partialSpotsRemaining })
            : t("scheduler_partial-spots-singular", { n: partialSpotsRemaining }))
        : "";
      const fullSpotsTextValue = fullSpotsRemaining > 0
        ? (partialSpotsTextValue !== ""
            ? fullSpotsText(fullSpotsRemaining)
            : spotsText(fullSpotsRemaining))
        : "";
      const parts = [];
      if (fullSpotsTextValue) parts.push(fullSpotsTextValue);
      if (partialSpotsTextValue) parts.push(partialSpotsTextValue);
      partialSignUpString = parts.length > 0 ? parts.join(and) : "";
      return t("scheduler_spots-still-open-notify", { spots: partialSignUpString });
    }

    return t("scheduler_spots-still-open-notify", { spots: spotsText(remaining) });
  };

  useEffect(() => {
    try {
      if (getOTWorkersStatus === "fulfilled") {
        if (
          Array.isArray(getOTWorkersResponse) &&
          getOTWorkersResponse?.length > 0 &&
          setAvailablePreferredWorkersForShiftRow
        ) {
          setAvailablePreferredWorkersForShiftRow((prevState) => {
            let newState: WorkersExtendedPropV2[] = [];
            const previousState = prevState ? [...prevState] : [];
            getOTWorkersResponse.forEach((preferredWorker) => {
              const selectedWorker = previousState.find(
                (worker) => worker.workerId === preferredWorker.workerId
              );
              if (!selectedWorker) {
                newState.push({
                  ...preferredWorker,
                  id: preferredWorker.workerId,
                  reason: t("scheduler_worker-no-longer-eligible-forced-overtime"),
                });
              }
            });
            previousState.forEach((worker) => {
              newState.push(worker);
            });
            newState = newState.filter((obj: any, index: number) => {
              return index === newState.findIndex((o: any) => obj.id === o.id);
            });
            newState = _.orderBy(newState, "order", ["asc"]);
            return newState;
          });
          setWorkersPreferredForAssignShift(
            [...getOTWorkersResponse]
              .sort((a, b) => a.workerId.localeCompare(b.workerId))
              .map((preferredWorker) => preferredWorker.workerId)
          );
          setOriginalWorkersPreferredForAssignShift(
            [...getOTWorkersResponse]
              .sort((a, b) => a.workerId.localeCompare(b.workerId))
              .map((preferredWorker) => preferredWorker.workerId)
          );
        }
      }
    } catch (e) {
      // Do nothing
    }
  }, [
    getOTWorkersResponse,
    getOTWorkersStatus,
    setAvailablePreferredWorkersForShiftRow,
    t,
  ]);

  useEffect(() => {
    if (setOTWorkersStatus === "error") {
      setWorkersPreferredForAssignShift(originalWorkersPreferredForAssignShift);
    }
  }, [setOTWorkersStatus, originalWorkersPreferredForAssignShift]);

  // A rejected assignment returns to Shift Details, where the reloaded
  // settings decide whether Assign Shift is still offered
  useEffect(() => {
    if (assignWorkersStatus === "error") {
      setWorkersSelectedForAssignShift([]);
      setWorkerSlotSelections({});
      setIAmSure?.(false);
      setActiveStep(SHIFT_DETAILS);
    }
  }, [assignWorkersStatus, setIAmSure]);

useEffect(() => {
  if (removeWorkerStatus === "fulfilled") {
    closeRemoveWorkerDialog?.();
  }
}, [removeWorkerStatus, closeRemoveWorkerDialog]);

  useEffect(() => {
    try {
      if (setOTWorkersStatus === "fulfilled") {
        if (
          Array.isArray(setOTWorkersResponse) &&
          setOTWorkersResponse.length > 0 &&
          setAvailablePreferredWorkersForShiftRow
        ) {
          setAvailablePreferredWorkersForShiftRow((prevState) => {
            let newState: WorkersExtendedPropV2[] = [];
            const previousState = prevState ? [...prevState] : [];
            previousState.forEach((worker) => {
              let newWorkerObject = { ...worker };
              setOTWorkersResponse.forEach((workerAndDecision) => {
                if (
                  workerAndDecision.workerId === worker.workerId &&
                  workerAndDecision.decision !== 1 &&
                  workerAndDecision.decision !== 0 &&
                  workerAndDecision.decision !== 2
                ) {
                  newWorkerObject = {
                    ...worker,
                    errorMessage: t(
                      "scheduler_unable-set-worker-forced-overtime"
                    ),
                  };
                }
              });
              newState.push(newWorkerObject);
            });
            newState = newState.filter((obj: any, index: number) => {
              return index === newState.findIndex((o: any) => obj.id === o.id);
            });
            newState = _.orderBy(newState, "order", ["asc"]);
            return newState;
          });
          const failedWorkers = setOTWorkersResponse.filter(
            (preferredWorker) =>
              preferredWorker.decision !== 1 &&
              preferredWorker.decision !== 0 &&
              preferredWorker.decision !== 2
          );
          if (failedWorkers && failedWorkers.length > 0) {
            setDetailPanelExpandedRowIds(
              failedWorkers.map((preferredWorker) => preferredWorker.workerId)
            );
          } else {
            setDetailPanelExpandedRowIds([]);
          }
        }
      }
    } catch (e) {
      // Do nothing
    }
  }, [
    setOTWorkersResponse,
    setOTWorkersStatus,
    setAvailablePreferredWorkersForShiftRow,
    t,
  ]);

  useEffect(() => {
    try {
      if (assignWorkersStatus === "fulfilled") {
        if (
          Array.isArray(assignWorkersResponse) &&
          setAvailableWorkersForShiftRow &&
          setRowsV2 &&
          setIAmSure
        ) {
          // All successfully assigned workers
          const successWorkers = assignWorkersResponse.filter(
            (assignedWorker) => assignedWorker.isSuccess
          );
          const successWorkersIds = successWorkers.map((successWorker) => {
            return successWorker.workerId;
          });

          // All workers having error while assigning
          const errorWorkers = assignWorkersResponse.filter(
            (assignedWorker) => !assignedWorker.isSuccess
          );
          const errorWorkersIds = errorWorkers.map((errorWorker) => {
            return errorWorker.workerId;
          });

          // Remove confirmed workers and show error to those who can not be assigned
          setAvailableWorkersForShiftRow((prevState) => {
            let newState: WorkersExtendedPropV2[] = [];
            const previousState = prevState ? [...prevState] : [];
            previousState.forEach((worker) => {
              if (errorWorkersIds.includes(worker.workerId)) {
                const selectedResponse = errorWorkers.find(
                  (errorWorker) => errorWorker.workerId === worker.workerId
                );
                if (selectedResponse) {
                  newState.push({
                    ...worker,
                    errorMessage: selectedResponse.message,
                  });
                }
              } else {
                if (!successWorkersIds.includes(worker.workerId)) {
                  newState.push(worker);
                }
              }
            });
            newState = _.orderBy(newState, "errorMessage", ["desc"]);
            newState = newState.filter((obj: any, index: number) => {
              return index === newState.findIndex((o: any) => obj.id === o.id);
            });
            newState = _.orderBy(newState, "order", ["asc"]);
            if (newState.length === 0) {
              return undefined;
            }
            return newState;
          });

          // Update main workers list with confirmed status
          setRowsV2((prevState) => {
            let newState: WorkersExtendedPropV2[] = [];
            prevState.forEach((worker) => {
              if (successWorkersIds.includes(worker.workerId) && !worker.justAssigned) {
                const selection = workerSlotSelections[worker.workerId as string];
                const isObject = typeof selection === "object";
                const slot = isObject ? selection.slot : (selection ?? 0);
                const isFirstHalfShift = 1 === slot;
                const isSecondHalfShift = 2 === slot;
                const isFlexiShift = 3 === slot;

                const shiftDuration = getHourMinuteDifference(
                  new Date(shiftMetadata?.endDate ?? shiftMetadata?.endDateTime),
                  new Date(shiftMetadata?.startDate ?? shiftMetadata?.startDateTime)
                );

                let workerShiftStartDateTime = shiftMetadata?.startDate ?? shiftMetadata?.startDateTime;
                let workerShiftEndDateTime = shiftMetadata?.endDate ?? shiftMetadata?.endDateTime;

                if (isSecondHalfShift) {
                  workerShiftStartDateTime = addMinutes(
                    new Date(shiftMetadata?.startDate ?? shiftMetadata?.startDateTime),
                    shiftDuration.differenceInMinutes/2
                  ).toISOString();
                } else if (isFlexiShift && isObject && selection.customStartTime) {
                  workerShiftStartDateTime = selection.customStartTime;
                }

                if (isFirstHalfShift) {
                  workerShiftEndDateTime = addMinutes(
                    new Date(shiftMetadata?.startDate ?? shiftMetadata?.startDateTime),
                    shiftDuration.differenceInMinutes/2
                  ).toISOString();
                } else if (isFlexiShift && isObject && selection.customEndTime) {
                  workerShiftEndDateTime = selection.customEndTime;
                }

                newState.push({
                  ...worker,
                  status: ASSIGNED,
                  workerShiftSlot: slot,
                  workerShiftStartDateTime,
                  workerShiftEndDateTime,
                  reason: "",
                  order: 2,
                  isAssigned: true,
                  justAssigned: true,
                });
              } else if (errorWorkersIds.includes(worker.workerId)) {
                const selectedResponse = errorWorkers.find(
                  (errorWorker) => errorWorker.workerId === worker.workerId
                );
                if (selectedResponse) {
                  newState.push({
                    ...worker,
                    errorMessage: selectedResponse.message,
                  });
                }
              } else {
                newState.push(worker);
              }
            });
            newState = newState.filter((obj: any, index: number) => {
              return index === newState.findIndex((o: any) => obj.id === o.id);
            });
            newState = _.orderBy(newState, "order", ["asc"]);
            return newState;
          });

          // If non fail then navigate to previous step
          const isFailed = assignWorkersResponse.find(
            (response) => !response.isSuccess
          );
          if (!isFailed) {
            setActiveStep(SHIFT_DETAILS);
          }
          setIAmSure(false);
        }
      }
    } catch (e) {
      console.log("EXCEPTION", e);
      // Do nothing
    }
  }, [assignWorkersResponse, assignWorkersStatus, setAvailableWorkersForShiftRow, setRowsV2, setIAmSure, workerSlotSelections, shiftMetadata?.startDate, shiftMetadata?.endDate, shiftMetadata?.startDateTime, shiftMetadata?.endDateTime]);

  useEffect(() => {
    // If we found any deselected workers, remove them from the slot selections state
    setWorkerSlotSelections(prevState => {
      // Get the IDs of all workers who currently have a slot selection
      const selectedSlotWorkerIds = Object.keys(prevState);

      // Find which of those workers are no longer in the main selection list
      const deselectedWorkerIds = selectedSlotWorkerIds.filter(
        (id) => !workersSelectedForAssignShift.includes(id)
      );
      if (deselectedWorkerIds.length > 0) {
        const newSelections = { ...prevState };
        deselectedWorkerIds.forEach((id) => {
          delete newSelections[id];
        });
        return newSelections;
      } else {
        return prevState;
      }
    });
  }, [workersSelectedForAssignShift]);

  const showPartialSignupView = activeStep === SHIFT_DETAILS
    && (shiftMetadata?.isPartialSignupEnabled || shiftMetadata?.isFlexiSignupEnabled)
    && activeTab === 0;

  const showSearchBar = (activeStep !== SHIFT_DETAILS
    || (activeTab === 1 || !((shiftMetadata?.isPartialSignupEnabled || shiftMetadata?.isFlexiSignupEnabled)
      && shiftMetadata?.shiftAllocationType === FASTEST))) && !showPartialSignupView

	return (
		<>
			<Dialog
				onClose={handleClose}
				aria-labelledby='add-new-shift'
				open={open}
				maxWidth={false}
			>
				<DialogTitle
					id='customized-dialog-title'
					maxWidth={1200}
					sx={{
						padding: 0,
					}}
				>
					{activeStep === SHIFT_DETAILS && (
						<>
              {shiftMetadata &&
                <ShiftMetaData
                  shiftMetadata={shiftMetadata}
                  jobRoleMetadata={jobRoleMetadata}
                  workCenterMetadata={workCenterMetadata}
                  allSkillLevels={allSkillLevels}
                  confirmedWorkersCount={confirmedWorkersCount}
                  showSkeleton={status === "pending"}
                  totalFullSpotsConfirmed={totalFullSpotsConfirmed}
                  totalHalfSpotsConfirmed={totalHalfSpotsConfirmed}
                  totalFlexiSpotsConfirmed={totalFlexiSpotsConfirmed}
                  totalFlexiSpotsTotal={totalFlexiSpotsTotal}
                />
              }
							{canShowWorkerPreferences() && (
								<Box
									sx={{
										padding: "12px 24px 12px 24px",
									}}
								>
									<Notice
										variant='normal'
										message={
											(originalWorkersPreferredForAssignShift?.length ?? 0) === 1
												? t("scheduler_workers-selected-forced-overtime-singular", {
														n: originalWorkersPreferredForAssignShift.length,
												  })
												: t("scheduler_workers-selected-forced-overtime-plural", {
														n:
															originalWorkersPreferredForAssignShift?.length ?? 0,
												  })
										}
										buttonLabel={t("scheduler_select-workers")}
										callToAction={() => {
											setWorkersPreferredForAssignShift([]);
											setActiveStep(SET_PREFERRED_WORKERS);
										}}
									/>
								</Box>
							)}
							{canShowNoticeForForceAssign() && (
								<Box
									sx={{
										padding: "12px 24px 0px 24px",
									}}
								>
									{/* In case there is no worker to assign when trying to assign worker */}
									{availableWorkersForShiftRow !== undefined &&
										availableWorkersForShiftRow.length === 0 && (
											<Notice
												message={t("scheduler_no-workers-available-assignment")}
												variant='error'
											/>
										)}
									{/* When there are some worker which can be assigned */}
									{availableWorkersForShiftRow !== undefined &&
										availableWorkersForShiftRow.length > 0 && (
											<Notice
												variant='normal'
												message={t("scheduler_not-enough-employees-signed-up")}
												buttonLabel={ASSIGN_SHIFT}
												callToAction={() => {
													setWorkersPreferredForAssignShift([]);
													setActiveStep(ASSIGN_SHIFT);
												}}
											/>
										)}
								</Box>
							)}
						</>
					)}
					{/* This header is for setting preferred worker*/}
					{activeStep === SET_PREFERRED_WORKERS && (
						<Box
							sx={{
                padding: "24px 24px 0px 24px",
								width: "100%",
								maxWidth: "1200px",
							}}
						>
							<Box
								mb={1.5}
								sx={{
									display: "flex",
									flexDirection: "row",
									alignItems: "flex-start",
									justifyContent: "flex-start",
								}}
							>
								<Box
									sx={{
										cursor: "pointer",
									}}
									onClick={() => {
										setWorkersPreferredForAssignShift(
											originalWorkersPreferredForAssignShift
										);
										setActiveStep(SHIFT_DETAILS);
									}}
								>
									<ArrowBack
										style={{
											color: "rgba(0, 0, 0, 0.87)",
											width: "32px",
											height: "32px",
											padding: "4px",
										}}
									/>
								</Box>
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "flex-start",
										justifyContent: "flexStart",
										marginLeft: "4px",
									}}
								>
									<Typography
										sx={{
											fontFamily: "Roboto",
											fontSize: "24px",
											fontWeight: "400",
											lineHeight: "31px",
											color: "rgba(0, 0, 0, 0.87)",
										}}
									>
                    {t("scheduler_select-workers-force-assign-open-spots")}
									</Typography>
									<Typography
										sx={{
											fontFamily: "Roboto",
											fontSize: "16px",
											fontWeight: "400",
											lineHeight: "28px",
											color: "rgba(0, 0, 0, 0.87)",
										}}
									>
										{appliedWorkersCount !== undefined &&
										appliedWorkersCount > 0
											? (parseInt(shiftMetadata.numberOfWorkersNeeded) > 1
													? t("scheduler_spots-open-plural", {
															n: parseInt(shiftMetadata.numberOfWorkersNeeded),
													  })
													: t("scheduler_spots-open-singular", {
															n: parseInt(shiftMetadata.numberOfWorkersNeeded),
													  }))
											: (parseInt(shiftMetadata?.numberOfWorkersNeeded) > 1
													? t("scheduler_spots-open-after-voluntary-plural", {
															n: parseInt(shiftMetadata?.numberOfWorkersNeeded),
													  })
													: t("scheduler_spots-open-after-voluntary-singular", {
															n: parseInt(shiftMetadata?.numberOfWorkersNeeded),
													  }))}
									</Typography>
								</Box>
							</Box>
						</Box>
					)}
					{/* This header is for assign shift to worker */}
					{activeStep === ASSIGN_SHIFT && (
						<Box
							sx={{
								padding: "24px 24px 0px 24px",
								width: "100%",
								maxWidth: "1200px",
							}}
						>
							<Box
								mb={1.5}
								sx={{
									display: "flex",
									flexDirection: "row",
									alignItems: "flex-start",
									justifyContent: "flex-start",
								}}
							>
								<Box
									sx={{
										cursor: "pointer",
									}}
									onClick={handleExitAssignShift}
								>
									<ArrowBack
										style={{
											color: "rgba(0, 0, 0, 0.87)",
											width: "32px",
											height: "32px",
											padding: "4px",
										}}
									/>
								</Box>
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "flex-start",
										justifyContent: "flexStart",
										marginLeft: "4px",
									}}
								>
									<Typography
										sx={{
											fontFamily: "Roboto",
											fontSize: "24px",
											fontWeight: "400",
											lineHeight: "31px",
											color: "rgba(0, 0, 0, 0.87)",
										}}
									>
                    {t("scheduler_force-assign-workers-open-spots")}
									</Typography>
									<Typography
										sx={{
											fontFamily: "Roboto",
											fontSize: "16px",
											fontWeight: "400",
											lineHeight: "28px",
											color: "rgba(0, 0, 0, 0.87)",
											marginTop: "12px",
										}}
									>
										{getWorkerCountTextForAssignShift()}
									</Typography>
								</Box>
							</Box>
						</Box>
					)}
				</DialogTitle>
				<DialogContent sx={{ maxWidth: "1200px", height: height - 280 - (canShowNoticeForForceAssign()
          && activeStep === SHIFT_DETAILS ? 82 : 0) - (
            canShowWorkerPreferences() && activeStep === SHIFT_DETAILS ? 75 : 0
          ), paddingBottom: "0px" }}>
					<>
            {activeStep === SHIFT_DETAILS &&
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab label={t("scheduler_confirmed-workers")} />
                <Tab label={t("scheduler_all-workers")} />
              </Tabs>
            }
						{ showPartialSignupView
							&& <ConfirmedWorkers
                parentHeight={height - 280 - (canShowNoticeForForceAssign()
                && activeStep === SHIFT_DETAILS ? 82 : 0) - (
                  canShowWorkerPreferences() && activeStep === SHIFT_DETAILS ? 75 : 0
                )}
								shiftMetadata={shiftMetadata}
                onClickRemoveWorker = {(worker :any) => handleRemoveWorker?.(worker)}
								confirmedWorkers={rowsV2?.filter(
									(worker) => (
										worker.status === CONFIRMED
										|| worker.status === ASSIGNED
									)
								)?.sort(
									// Show worker working full shift time first then partially accepted one
									(worker) => {
										const workerShiftStartDateTime = worker.workerShiftStartDateTime;
										const workerShiftEndDateTime = worker.workerShiftEndDateTime;
										if (workerShiftStartDateTime && workerShiftEndDateTime) {
											const startTimeDiff = getHourMinuteDifference(
												new Date(workerShiftStartDateTime),
												new Date(shiftMetadata?.startDateTime)
											);
											const endTimeDiff = getHourMinuteDifference(
												new Date(workerShiftEndDateTime),
												new Date(shiftMetadata?.endDateTime)
											);
											const isFullyAccepted =
												startTimeDiff.differenceInMinutes === 0 &&
												endTimeDiff.differenceInMinutes === 0
											;
											return isFullyAccepted ? 0 : 1;
										}
										return 1;
									}
								)} loadingStatus={status} />}
						{ showSearchBar && (
							<>
								{/* Render search only when we have some rows in table */}
								{((activeStep === ASSIGN_SHIFT &&
										availableWorkersForShiftRow !== undefined &&
										availableWorkersForShiftRow.length > 0) ||
									((activeStep === SHIFT_DETAILS ||
											activeStep === SET_PREFERRED_WORKERS) &&
										rowsV2 !== undefined &&
										rowsV2.length > 0)) && (
									<Box
										sx={{
											display: "flex",
											flexDirection: "row",
											alignItems: "center",
											height: "57px",
											width: "100%",
											gap: 2,
										}}
									>
										{/* Currently Rendering filter only when we are in shift details step */}
										{activeStep === SHIFT_DETAILS && (
											<>
												<Box
													sx={{
														flex: 0,
														display: "flex",
														justifyContent: "center",
														alignItems: "center",
														p: 0.5,
														borderRadius: "4px",
														background: "#0000000A",
														":hover": {
															cursor: "pointer",
															background: "#0000001F",
														},
													}}
													onClick={(e) => {
														setFilterAnchorEl(e.currentTarget);
													}}
												>
													<FilterListIcon />
												</Box>
												<CheckboxPopover
													anchorElement={filterAnchorEl}
													options={
                            activeTab === 0
                              ? statusOptions?.filter((data: any) => (data?.label === CONFIRMED || data?.label === ASSIGNED))
                              : statusOptions
                          }
													setOptions={setStatusOptions}
													onClose={() => {
														setFilterAnchorEl(null);
													}}
													selectAllCheckboxOption={false}
													minWidth={220}
													showCount
													disableMaxHeight
												/>
											</>
										)}
										<Box
											sx={{
												flex: 0,
												display: "flex",
												justifyContent: "center",
												alignItems: "center",
												p: 0.5,
												borderRadius: "4px",
												background: "#0000000A",
												":hover": {
													cursor: "pointer",
													background: "#0000001F",
												},
											}}
											onClick={(e) => {
												setColumnAnchorEl(e.currentTarget);
											}}
										>
											<ViewColumnOutlinedIcon />
										</Box>
										<CheckboxPopover
											anchorElement={columnAnchorEl}
                      options={columnOptions?.filter((data :any) => data.headerName !== "Actions")}
                      setOptions={setColumnOptions}
											onClose={() => {
												setColumnAnchorEl(null);
											}}
											selectAllCheckboxOption={false}
											minWidth={220}
											disableMaxHeight
										/>
										<Box sx={{ flex: 1 }}>
											<TextField
												hiddenLabel
												value={search}
												onChange={(e) => {
													try {
														const text = e.target.value;
														setSearch(text);
													} catch (error) {
														// Do nothing
													}
												}}
												placeholder={t("common_search")}
												variant='filled'
												size='small'
												InputProps={{
													disableUnderline: true,
													startAdornment: (
														<SearchIcon
															style={{
																height: "24px",
																width: "24px",
																color: "rgba(0,0,0,0.56)",
															}}
														/>
													),
													endAdornment:
														search !== "" ? (
															<ClearIcon
																onClick={() => {
																	setSearch("");
																}}
																style={{
																	height: "24px",
																	width: "24px",
																	color: "rgba(0,0,0,0.56)",
																	cursor: "pointer",
																}}
															/>
														) : (
															<></>
														),
												}}
												sx={{
													width: "100%",
													border: "1px solid rgba(0,0,0,0.12)",
													borderRadius: "4px",
													input: {
														paddingLeft: "8px",
														color: "rgba(0,0,0,0.87)",
														fontFamily: "Roboto",
														fontSize: "12px",
														fontWeight: "400",
														lineHeight: "20px",
														letterSpacing: "0.4px",
													},
												}}
											/>
										</Box>
									</Box>
								)}
								{activeStep === SET_PREFERRED_WORKERS && (
									<AssignShiftTable
                    key={activeStep}
                    rows={availablePreferredWorkersForShiftRow ?? []}
										columns={columns}
										setWorkersSelected={setWorkersPreferredForAssignShift}
										searchValue={search}
										workersSelected={workersPreferredForAssignShift}
										detailPanelExpandedRowIds={detailPanelExpandedRowIds}
									/>
								)}
								{activeStep === ASSIGN_SHIFT && (
									<AssignShiftTable
                    key={activeStep}
                    rows={availableWorkersForShiftRow ?? []}
										shiftMetadata={shiftMetadata}
										columns={columns}
										setWorkersSelected={setWorkersSelectedForAssignShift}
										readOnly={!canManualAssignWorkers}
										searchValue={search}
										workersSelected={workersSelectedForAssignShift}
										totalWorkersRequired={
											confirmedWorkersCount !== undefined
												? parseInt(shiftMetadata.numberOfWorkersNeeded) -
												confirmedWorkersCount
												: parseInt(shiftMetadata.numberOfWorkersNeeded) -
												parseInt(shiftMetadata.slotsFilled)
										}
                    canSelectRow={
                      availableSlots?.firstHalf !== 0 ||
                      availableSlots?.secondHalf !== 0 ||
                      availableSlots?.fullShift !== 0 ||
                      (shiftMetadata?.isFlexiSignupEnabled &&
                        openFlexiSlots &&
                        openFlexiSlots.length > 0)
                    }
										detailPanelExpandedRowIds={detailPanelExpandedRowIds}
									/>
								)}
								{(activeStep === SHIFT_DETAILS && !showPartialSignupView) && (
									<ShiftDetailsTable
                    key={activeStep}
                    parentHeight={height - 280 - (canShowNoticeForForceAssign()
                    && activeStep === SHIFT_DETAILS ? 82 : 0) - (
                      canShowWorkerPreferences() && activeStep === SHIFT_DETAILS ? 75 : 0
                    )}
										rows={activeTab === 0 ? rowsV2?.filter(
												(worker) => (
													worker.status === CONFIRMED
													|| worker.status === ASSIGNED
												))
											: rowsV2}
										columns={columns?.filter((column: any) => column.checked)}
										setWorkersSelected={setWorkersSelected}
										isShiftPast={isPast(new Date(shiftMetadata?.endDateTime))}
										readOnly={!canRenotifyWorkers}
										searchValue={search}
										hasOpenSpots={hasOpenSpots}
										statusOptions={statusOptions}
										loadingStatus={status}
										activeTab={activeTab}
									/>
								)}
							</>
						)}
					</>
				</DialogContent>
				<Box
					display='flex'
					alignItems='center'
					justifyContent='space-between'
					pt={3}
					pb={3}
					pl={3}
					pr={3}
					sx={{
						borderTop: 1,
						borderColor: "rgba(0, 0, 0, 0.12)",
					}}
					maxWidth={"1200px"}
				>
					{activeStep !== SHIFT_DETAILS && <Box />}
					{activeStep === SHIFT_DETAILS &&
						(status === "pending" ? (
							<Skeleton width={400} height={20} />
						) : (
							<Box style={{ marginRight: "24px" }}>
								<Typography
									variant='body2'
									sx={{
										fontFamily: "Roboto",
										fontSize: "14px",
										fontWeight: "400",
										lineHeight: "20px",
										color: "#00000099",
									}}
								>
									{shiftMetadata?.createdDate
										? t("scheduler_published-by-on-date", {
												name: shiftMetadata?.publishedByName,
												date: formatDate({
													dateStr: shiftMetadata?.publishedOn ?? "",
													formatType: "CAPTION_DATE",
												}),
												time: formatDate({
													dateStr: shiftMetadata?.publishedOn ?? "",
													formatType: "TIME",
												}),
										  })
										: t("scheduler_published-by", {
												name: shiftMetadata?.publishedByName,
										  })}
                </Typography>
              </Box>
            ))}
          {activeStep === ASSIGN_SHIFT && (
            <ReviewWorkerAssignShiftCheckbox
              iAmSure={iAmSure}
              setIAmSure={setIAmSure}
            />
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            {activeStep === SHIFT_DETAILS && (
              <>
                <Button variant="text" onClick={() => handleClose()}>
                  {t("common_cancel")}
                </Button>
                <Button
                  autoFocus
                  color="primary"
                  variant="contained"
                  sx={{
                    marginLeft: "8px",
                  }}
                  disabled={
                    !canRenotifyWorkers ||
                    !hasOpenSpots ||
                    isPast(new Date(shiftMetadata?.startDateTime))
                      ? true
                      : workersSelected.length === 0 ||
                        renotifyWorkersStatus === "pending" ||
                        renotifyWorkersStatus === "error"
                  }
                  onClick={() =>
                    dispatch(
                      renotifyWorkers({ workers: workersSelected, shiftId })
                    )
                  }
                >
                  {renotifyWorkersStatus === "pending" ? (
                    <Box position="relative">
                      <CircularProgress
                        size={25}
                        style={{ position: "absolute", left: "50%" }}
                      />
                      {t("scheduler_renotifying")}
                    </Box>
                  ) : (
                    t("scheduler_renotify-workers")
                  )}
                </Button>
              </>
            )}
            {activeStep === SET_PREFERRED_WORKERS && (
              <>
                <Button
                  variant="text"
                  onClick={() => {
                    setWorkersPreferredForAssignShift(
                      originalWorkersPreferredForAssignShift
                    );
                    setActiveStep(SHIFT_DETAILS);
                  }}
                >
                  {t("common_cancel")}
                </Button>
                <LoadingButton
                  fullWidth
                  sx={{
                    marginLeft: "8px",
                  }}
                  disabled={
                    !canManualAssignWorkers ||
                    isPast(new Date(shiftMetadata?.startDateTime))
                      ? true
                      : setOTWorkersStatus === "pending" ||
                        JSON.stringify(
                          originalWorkersPreferredForAssignShift
                        ) === JSON.stringify(workersPreferredForAssignShift)
                  }
                  variant="contained"
                  loading={setOTWorkersStatus === "pending"}
                  onClick={() => {
                    const workers =
                      workersPreferredForAssignShift.map((worker) => {
                        return {
                          workerId: worker,
                        };
                      }) ?? [];
                    if (workers) {
                      dispatch(
                        setOTWorkers({
                          workers: workers,
                          shiftId: shiftId,
                          workersPreferredForAssignShift:
                            workersPreferredForAssignShift,
                        })
                      );
                    }
                  }}
                  loadingPosition="start"
                  startIcon={<></>}
                  endIcon={<></>}
                  loadingIndicator={
                    <CircularProgress
                      size={16}
                      sx={{
                        color: "rgba(0, 0, 0, 0.38)",
                        marginLeft: "12px",
                      }}
                    />
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "Roboto",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "24px",
                        letterSpacing: "0.46px",
                        textTransform: "uppercase",
                      }}
                    >
                      {setOTWorkersStatus === "pending" ? t("scheduler_saving") : t("common_save")}
                    </span>
                  </div>
                </LoadingButton>
              </>
            )}
            {activeStep === ASSIGN_SHIFT && (
              <>
                <Button
                  variant="text"
                  onClick={handleExitAssignShift}
                >
                  {t("common_cancel")}
                </Button>
                <LoadingButton
                  fullWidth
                  sx={{
                    marginLeft: "8px",
                  }}
                  disabled={
                    !canManualAssignWorkers ||
                    isPast(new Date(shiftMetadata?.startDateTime))
                      ? true
                      : workersSelectedForAssignShift.length === 0 ||
                        assignWorkersStatus === "pending" ||
                        !iAmSure
                  }
                  variant="contained"
                  loading={assignWorkersStatus === "pending"}
                  onClick={() => {
                    // Create the payload with workerId and its selected slot
                    const workersWithSlots = workersSelectedForAssignShift.map((workerId) => {
                      const selection = workerSlotSelections[workerId as string];
                      const isObject = typeof selection === "object";
                      const slot = isObject ? selection.slot : (selection ?? 0);
                      return {
                        id: workerId as string,
                        workerId: workerId as string,
                        slot: slot,
                        workerShiftStartDateTime: (isObject && selection.customStartTime) ? new Date(selection.customStartTime).toISOString() : undefined,
                        workerShiftEndDateTime: (isObject && selection.customEndTime) ? new Date(selection.customEndTime).toISOString() : undefined,
                      };
                    });
                    dispatch(
                      assignWorkers({
                        workers: workersWithSlots,
                        shiftId: shiftId,
                      })
                    );
                  }}
                  loadingPosition="start"
                  startIcon={<></>}
                  endIcon={<></>}
                  loadingIndicator={
                    <CircularProgress
                      size={16}
                      sx={{
                        color: "rgba(0, 0, 0, 0.38)",
                        marginLeft: "12px",
                      }}
                    />
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "Roboto",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "24px",
                        letterSpacing: "0.46px",
                        textTransform: "uppercase",
                      }}
                    >
                      {assignWorkersStatus === "pending"
                        ? t("scheduler_assigning")
                        : t("scheduler_assign")}
                    </span>
                  </div>
                </LoadingButton>
              </>
            )}
          </Box>
        </Box>
        {setOTWorkersStatus === "fulfilled" &&
        getPreferredWorkerAssignedSuccessMessage() !== "" ? (
          <Snackbar
            open={setOTWorkersStatus === "fulfilled"}
            autoHideDuration={3000}
            onClose={() => {
              dispatch(resetSetOTWorkers());
              dispatch(resetSetOTWorkersResponse());
            }}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            TransitionComponent={Transition}
          >
            <MuiAlert elevation={6} variant="filled" severity="success">
              {getPreferredWorkerAssignedSuccessMessage()}
            </MuiAlert>
          </Snackbar>
        ) : null}
        {removeWorkerDialogOpen ? (
          <RemoveShiftWorkerDialog
           shiftId ={shiftId}
            isDilogueOpen={removeWorkerDialogOpen}
            worker={removeWorkerDetail}
            handleClose= {() => closeRemoveWorkerDialog}
          />
        ) : null}
        {assignWorkersStatus === "fulfilled" &&
        getAssignedWorkerSuccessMessage() !== "" ? (
          <Snackbar
            open={assignWorkersStatus === "fulfilled"}
            autoHideDuration={3000}
            onClose={() => {
              dispatch(resetAssignWorkers());
              dispatch(resetAssignWorkersResponse());
            }}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert elevation={6} variant="filled" severity="success">
              {getAssignedWorkerSuccessMessage()}
            </MuiAlert>
          </Snackbar>
        ) : null}
        {status === "error" ? (
          <Snackbar
            open={status === "error"}
            autoHideDuration={5000}
            onClose={() => dispatch(resetShiftDetails())}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert
              elevation={6}
              variant="filled"
              severity="error"
              onClose={handleClose}
            >
              <Box display="flex" flexDirection="column" alignItems="center">
                <Box mb={1}>{errorMessage ?? t("common_error-occurred")}</Box>
              </Box>
            </MuiAlert>
          </Snackbar>
        ) : null}
      </Dialog>

      <Snackbar
				open={ removeWorkerStatus === "fulfilled"}
				autoHideDuration={3000}
				onClose={() => {
          closeRemoveWorkerDialog?.()
          dispatch(resetRemoveWorker())
        }
        }
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert elevation={6} variant='filled' severity='success'>
					{t("scheduler_worker-removed-successfully")}
				</Alert>
			</Snackbar>

			<Snackbar
				open={removeWorkerStatus === "error"}
				autoHideDuration={3000}
				onClose={() => { closeRemoveWorkerDialog?.()
          dispatch(resetRemoveWorker() 
          )
        }}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert elevation={6} variant="filled" severity="error" onClose={ () =>         closeRemoveWorkerDialog?.()
}>
					<Box display="flex" flexDirection="column" alignItems="center">
						<Box mb={1}>
							{t("common_error-try-again")}
						</Box>
					</Box>
				</Alert>
			</Snackbar>
    </>
  );
};

export default ShiftDetailsDialog;
